import { Box, HStack, VStack, Text, Badge, Switch } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { ScheduledItemDto, ScheduleTimeBlockDto } from '../types';
import { hhmm } from '../utils';

function ItemSection({
  title,
  items,
  onToggleExclusion,
  busy,
}: {
  title: string;
  items: ScheduledItemDto[];
  onToggleExclusion: (scheduleItemId: string, excluded: boolean) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <Box mt={3}>
      <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>
        {title}
      </Text>
      <VStack align="stretch" spacing={1}>
        {items.map((item) => {
          const meta = item.displayMeta;
          const label =
            meta.scheduleMode === 'frequency'
              ? t('schedule.perHour', { count: meta.frequencyPerHour ?? 0 })
              : (meta.specificTimes ?? []).join(', ');
          return (
            <HStack key={item.scheduleItemId} justify="space-between" opacity={item.excluded ? 0.5 : 1}>
              <Box minW={0}>
                <Text fontSize="sm" color="gray.800" noOfLines={1}>
                  {item.name}
                </Text>
                <Text fontSize="xs" color="gray.400">
                  {label}
                </Text>
              </Box>
              <Switch
                size="sm"
                colorScheme="brand"
                isChecked={!item.excluded}
                isDisabled={busy}
                onChange={(e) => onToggleExclusion(item.scheduleItemId, !e.target.checked)}
              />
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
}

interface ScheduleBlockCardProps {
  block: ScheduleTimeBlockDto;
  onToggleExclusion: (scheduleItemId: string, excluded: boolean) => void;
  busy: boolean;
}

export function ScheduleBlockCard({ block, onToggleExclusion, busy }: ScheduleBlockCardProps) {
  const { t } = useTranslation();
  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
      <HStack justify="space-between" mb={2}>
        <Text fontWeight="700" color="gray.800">
          {hhmm(block.startTime)}–{hhmm(block.endTime)}
        </Text>
        <Badge colorScheme="blue">{block.scenarioName}</Badge>
      </HStack>

      {block.playlists.length > 0 && (
        <Box>
          <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>
            {t('schedule.music')}
          </Text>
          <VStack align="stretch" spacing={0.5}>
            {block.playlists.map((p) => (
              <Text key={p.id} fontSize="sm" color="gray.700" noOfLines={1}>
                {p.name} · {t('playlists.trackCountLabel', { count: p.trackCount })}
              </Text>
            ))}
          </VStack>
        </Box>
      )}

      <ItemSection title={t('schedule.ads')} items={block.ads} onToggleExclusion={onToggleExclusion} busy={busy} />
      <ItemSection
        title={t('schedule.special')}
        items={block.specialAnnouncements}
        onToggleExclusion={onToggleExclusion}
        busy={busy}
      />
    </Box>
  );
}

export default ScheduleBlockCard;
